import React, { useContext, useEffect, useState } from 'react'
import { Link } from 'gatsby'
import classNames from 'classnames'
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry'
import { I18nextContext } from '@toasttab/shared/src/context/i18nextContext'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import { localizeLink } from '@toasttab/shared/src/utils/linkUtils'
import FooterContentColumn from './FooterContentColumn'
import MainRegionSelector from './MainRegionSelector'
import RegionSelector from './RegionSelector'
import FooterSocialIcons from './FooterSocialIcons'
import FooterLegalLinks from './FooterLegalLinks'
import { prependHostName } from '@toasttab/shared/src/utils/siteUtils'
import { FooterProps } from './Footer.d'
import { resolvePrefix } from '@toasttab/shared/src/utils/assetUtils'
import { getAppStoreBadge } from '@toasttab/shared/src/utils/appStoreBadgeUtiils'
import { useRegionSwitcherRedesign } from '@toasttab/shared/src/utils/featureFlags'
import RegionLanguageSelector from './RegionLanguageSelector'

const Footer = ({
  currentLanguage,
  footerContent,
  hostName,
  localizedEntries,
  packageName,
  siteId,
  siteMetadata
}: FooterProps) => {
  const [isClientRender, setIsClientRender] = useState(false)
  const { t } = useI18next()
  const localizationContext = useContext(I18nextContext)

  useEffect(() => {
    if (typeof window !== 'undefined' && !isClientRender) {
      setIsClientRender(true)
    }
  }, [])

  const renderAppIcon = (icon: string) => {
    if (!icon) {
      return null
    }
    const iconMap = getAppStoreBadge(localizationContext.language, {
      appStoreAlt: t('Download on the App Store'),
      googlePlayAlt: t('Get it on Google Play')
    })
    const iconContent = iconMap[icon] ?? null
    if (iconContent) {
      return (
        <img
          alt={iconContent.alt}
          className='h-8'
          height='32'
          src={iconContent.src}
        />
      )
    }
    return null
  }

  const footerLinkCss =
    'text-secondary font-sansPro font-normal text-base hover:text-default'
  const mainLinkWrapperCss = classNames(
    'col-span-12 md:col-span-6 lg:col-span-8',
    {
      // The [&>div>div>div] garbage is a hack to fix the grid spacing on the masonry plugin, because the gutter prop isn't working in the Masonry component
      '[&>div>div]:!gap-6 [&>div>div>div]:!gap-8': isClientRender,
      'grid gap-8': !isClientRender
    }
  )

  const borderColor =
    packageName === 'onTheLine' ? 'border-color-light' : 'border-light'

  // Prepend https://pos.toasttab.com for support.toasttab.com
  const footerData =
    packageName === 'support'
      ? prependHostName(footerContent, siteMetadata.siteUrl)
      : footerContent

  return (
    <footer
      className='bg-gray-0 pb-32 lg:pb-0'
      data-testid='Footer'
      id='main-footer'
      role='contentinfo'
    >
      {footerData?.menu && (
        <div
          className={`ds-footer-container flex justify-center border-b ${borderColor}`}
          data-testid='Footer-mainContent'
        >
          <div className='grid grid-flow-row-dense grid-cols-12 gap-6 pt-10 md:pt-16 pb-4 lg:pb-10 flex-1'>
            <div className='row-span-2 lg:row-span-1 text-light col-span-12 md:col-span-6 lg:col-span-4 mb-8'>
              <Link
                to={packageName === 'support' ? hostName : '/'}
                className='logo w-[120px] mb-6 block'
              >
                <img
                  alt={footerData.title}
                  src={resolvePrefix('/svg/img-toast-logo.svg')}
                />
              </Link>
              {footerData.companyContact && (
                <ul className='mb-4 md:mb-6'>
                  <li className='text-base text-secondary font-sansPro'>
                    {footerData.companyContact.address?.map((address: any) => (
                      <p key={address}>{t(address)}</p>
                    ))}
                  </li>
                  {footerData.companyContact.phoneNumbers?.map((phone: any) => (
                    <li key={phone.label}>
                      <a href={`tel:${phone.number}`} className={footerLinkCss}>
                        {t(phone.label)}: {phone.number}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
              {footerData.socials && (
                <FooterSocialIcons socialIcons={footerData.socials} />
              )}
            </div>
            {/* The isClientRender check is to fix hydration errors when the Masonry component. So we only enable it on the client, not for SSR */}
            <div className={mainLinkWrapperCss}>
              {isClientRender ? (
                <ResponsiveMasonry
                  columnsCountBreakPoints={{ 240: 2, 768: 2, 1024: 4 }}
                >
                  <Masonry gutter='24px'>
                    {footerData.menu.map((menu: any) => (
                      <FooterContentColumn
                        footerLinkCss={footerLinkCss}
                        hostName={hostName}
                        key={menu.title}
                        title={menu.title}
                        menuItems={menu.menuItems}
                      />
                    ))}
                  </Masonry>
                </ResponsiveMasonry>
              ) : (
                <div className='grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-6'>
                  {footerData.menu.map((menu: any) => (
                    <FooterContentColumn
                      footerLinkCss={footerLinkCss}
                      hostName={hostName}
                      key={menu.title}
                      title={menu.title}
                      menuItems={menu.menuItems}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {(footerData?.showRegionSelector || footerData?.appLinks) && (
        <div
          className={`ds-footer-container md:flex items-center py-4 md:pt-8 md:pb-6 border-b ${borderColor}`}
        >
          {footerData.showRegionSelector && (
            <>
              {/* Using the new RegionLanguageSelector for otl and support */}
              {!['main', 'support'].includes(packageName) &&
                (useRegionSwitcherRedesign ? (
                  <RegionLanguageSelector
                    currentLanguage={currentLanguage}
                    footerLinkCss={footerLinkCss}
                    hostName={hostName}
                    localizedEntries={localizedEntries}
                    packageName={packageName}
                    siteId={siteId}
                  />
                ) : (
                  <RegionSelector
                    currentLanguage={currentLanguage}
                    footerLinkCss={footerLinkCss}
                    localizedEntries={localizedEntries}
                    packageName={packageName}
                    siteId={siteId}
                  />
                ))}
              {/* Using the new RegionLanguageSelector for main */}
              {packageName === 'main' &&
                (useRegionSwitcherRedesign ? (
                  <RegionLanguageSelector
                    currentLanguage={currentLanguage}
                    footerLinkCss={footerLinkCss}
                    hostName={hostName}
                    localizedEntries={localizedEntries}
                    packageName={packageName}
                    siteId={siteId}
                  />
                ) : (
                  <MainRegionSelector
                    currentLanguage={currentLanguage}
                    footerLinkCss={footerLinkCss}
                    hostName={hostName}
                    localizedEntries={localizedEntries}
                    packageName={packageName}
                    siteId={siteId}
                  />
                ))}
            </>
          )}
          {footerData.appLinks && (
            <div
              className={`ml-auto flex border-t md:border-0 pt-4 mt-4 md:pt-0 md:mt-0 ${borderColor}`}
            >
              {footerData.appLinks.map((content: any, i: number) => (
                <a
                  className={i === 0 ? 'mr-2 md:mr-4' : null}
                  href={localizeLink(
                    content.href,
                    localizationContext.language,
                    localizationContext
                  )}
                  key={content.icon}
                  rel='nofollow'
                >
                  {renderAppIcon(content.icon)}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
      {footerData?.legal && (
        <FooterLegalLinks hostName={hostName} legalLinks={footerData.legal} />
      )}
    </footer>
  )
}

export default Footer
